import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import axios from 'axios';
import AuthGate from '../components/AuthGate';
import RequirePermission from '../components/RequirePermission';
import PageErrorBoundary from '../components/PageErrorBoundary';
import HubLayout from './HubLayout';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

const MyWorkshops = lazy(() => import('../components/my/MyWorkshops'));
const RecordingsPage = lazy(() => import('../components/my/RecordingsPage'));
const SESummitsPage = lazy(() => import('../components/my/SESummitsPage'));
const NoShowAction = lazy(() => import('../components/my/NoShowAction'));

const API_BASE = process.env.REACT_APP_API_URL || '';
axios.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401 && process.env.REACT_APP_AUTH_DISABLED !== 'true') {
      const redirect = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `${API_BASE}/auth/saml?redirect=${redirect}`;
    }
    return Promise.reject(err);
  }
);

const PageFallback = (
  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
    <CircularProgress />
  </Box>
);

// Plan 2184 links (/my?recording=<slug>) predate the Session Recordings page.
function MyOrRecordings() {
  const { search } = useLocation();
  if (new URLSearchParams(search).has('recording')) return <Navigate to="/recordings" replace />;
  return <MyWorkshops />;
}

const guarded = (el) => (
  <RequirePermission resource="self"><PageErrorBoundary>{el}</PageErrorBoundary></RequirePermission>
);

export default function HubApp() {
  return (
    <AuthGate>
      <Suspense fallback={PageFallback}>
        <Routes>
          <Route element={<HubLayout />}>
            <Route path="/my" element={guarded(<MyOrRecordings />)} />
            <Route path="/recordings" element={guarded(<RecordingsPage />)} />
            <Route path="/se-summits" element={guarded(<SESummitsPage />)} />
            <Route path="/my/help" element={guarded(<MyWorkshops />)} />
            <Route path="/my/no-show/:logId" element={guarded(<NoShowAction />)} />
          </Route>
          {/* Outside HubLayout so a redirect-only hop never mounts usePageTracking (no ghost "/" pageview). */}
          <Route path="*" element={<Navigate to="/my" replace />} />
        </Routes>
      </Suspense>
    </AuthGate>
  );
}
